import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import { UpdateContactModel } from "../factories/contacts/models/update-contact.model";
import { UpdateOrganizationModel } from "../factories/contacts/models/update-organization.model";
import { CustomerModel } from "../factories/customers/models/customerModel";
import { billingAPI } from "../services/BillingService";
import {
  IContact,
  IContactProfile,
  IContactType,
  IOrganizationContact,
  IOrganizationType,
  IPhoneType,
  SelectOptionType,
} from "../types/types";
import { contactNotesSlice } from "./ContactNotesSlice";
import { filesSlice } from "./FilesSlice";
import { linkedProfilesSlice } from "./LinkedProfilesSlice";
import { organizationNotesSlice } from "./OrganizationNotesSlice";
import { workSlice } from "./WorkSlice";

export type AddIndividualFormData = {
  customerType: {
    id: string;
  };
};

export type AddOrganizationFormData = {
  contactStatusType: {
    id: string;
  };
  contactTypes: {
    id: string;
    contactType: string;
  }[];
};

type ContactEmailRequest = {
  emailAddress: string;
  isPrimaryEmail: boolean;
};

type AddIndividualContactRequest = {
  customerTypeEnumId?: string;
  orgId?: string;
  locationId: string | null;
  email: string;
  isPrimaryEmail: boolean;
  firstName: string;
  lastName: string;
  middleName: string;
  displayName: string;
  socialIdentificationNumber: string;
  phoneNumberTypeEnumId?: string;
  contactTypeEnumId?: string;
  phoneNumber: string;
};

type AddOrganizationContactRequest = {
  companyName: string;
  businessNumber: string;
  contactTypeEnumId?: string;
  locationId: string | null;
  contactStatusEnumId?: string;
  contactPersons: {
    firstName: string;
    lastName: string;
    emails: ContactEmailRequest[];
    phoneNumbers: {
      number: string;
      phoneNumberTypeEnumId: string;
    }[];
  };
  contactPersonFirstName: string;
  contactPersonLastName: string;
  contactPersonEmailAddress: string[];
  contactPersonPhoneNumber: string[];
};

type ChangeContactPrimaryEmailRequest = {
  contactId: string;
  emailId: string;
};

type ChangeOrganizationPrimaryEmailRequest = {
  businessContactId: string;
  emailId: string;
};

export type ClientEmailMatch = {
  email: string;
  customerId: string;
  name: string;
  type: "organization" | "individual";
  contactPersonName?: string | null;
  link: string;
};

export const contactsSlice = createApi({
  reducerPath: "contactsSlice",
  tagTypes: [
    "Contacts",
    "ContactNotes",
    "ContactFiles",
    "OrgContacts",
    "ContactsWorks",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    //Contacts
    getAllContacts: build.query<IContact[], string>({
      query: (orgId = "") => ({
        url: "api/contacts/search",
        params: {
          orgId: orgId,
        },
      }),
      providesTags: () => ["Contacts"],
    }),
    addPicture: build.mutation<
      unknown,
      { contactId?: string; businessContactId?: string; body: FormData }
    >({
      query: ({ contactId, businessContactId, body }) => ({
        url: "api/customers/add-image",
        method: "POST",
        params: { contactId, businessContactId },
        body,
      }),
      invalidatesTags: ["Contacts", "OrgContacts"],
    }),
    editPicture: build.mutation<
      unknown,
      { contactId?: string; businessContactId?: string; body: FormData }
    >({
      query: ({ contactId, businessContactId, body }) => ({
        url: "api/contacts/update-individual",
        method: "PUT",
        params: { contactId, businessContactId },
        body,
      }),
      invalidatesTags: ["Contacts", "OrgContacts"],
    }),
    deletePicture: build.mutation<
      unknown,
      { contactId?: string; businessContactId?: string }
    >({
      query: ({ contactId, businessContactId }) => ({
        url: "api/customers/remove-image",
        method: "DELETE",
        params: { contactId, businessContactId },
      }),
      invalidatesTags: ["Contacts", "OrgContacts"],
    }),
    getActiveCustomers: build.query<
      CustomerModel[],
      {
        orgId: string;
        isActive: boolean;
        userId: string;
        tagIds: string[];
        yearEndMonth: string[];
        customerName: string | null;
        sortOrder: "asc" | "desc";
        sortColumn: string | null;
        searchQuery?: string | null;
        pageSize?: number;
        pageNumber?: number;
        organizationTypeIds?: string[] | null;
        /** Client team filters (Mg-231): people ids. */
        clientOwnerUserIds?: string[];
        clientManagerUserIds?: string[];
      }
    >({
      query: (body) => ({
        url: "api/customers/get-active-or-inactive",
        method: "POST",
        body,
      }),

      providesTags: () => ["Contacts"],
    }),
    getArchivedCustomers: build.query<
      CustomerModel[],
      {
        orgId: string;
        isActive: boolean;
        userId: string;
        tagIds: string[];
        yearEndMonth: string[];
        customerName: string | null;
        sortOrder: "asc" | "desc";
        sortColumn: string | null;
        searchQuery?: string | null;
        pageSize?: number;
        pageNumber?: number;
        /** Required by the API (an empty list = all types). */
        organizationTypeIds: string[];
      }
    >({
      query: (body) => ({
        url: "api/customers/get-active-or-inactive",
        method: "POST",
        body,
      }),
      providesTags: () => ["Contacts"],
    }),
    activateDeactivate: build.mutation<
      { data: CustomerModel[] },
      { body: string[]; activateCustomer: boolean }
    >({
      query: ({ body, activateCustomer }) => ({
        url: "api/customers/activate-or-deactivate",
        method: "Put",
        body,
        params: { activateCustomer },
      }),
      invalidatesTags: ["OrgContacts", "Contacts"],
    }),
    getActiveContacts: build.query<IContact[], string>({
      query: (orgId = "") => ({
        url: "api/contacts/search",
        params: {
          orgId: orgId,
        },
      }),
      transformResponse: (res: IContact[]) => {
        return (
          res?.filter(
            (item) =>
              item.statusType && item.statusType.statusType === "Active",
          ) || []
        );
      },
      providesTags: () => ["Contacts"],
    }),
    getArchivedContacts: build.query<IContact[], string>({
      query: (orgId = "") => ({
        url: "api/contacts/search",
        params: {
          orgId: orgId,
        },
      }),
      transformResponse: (res: IContact[]) => {
        return (
          res?.filter(
            (item) =>
              item.statusType && item.statusType.statusType === "Unknown",
          ) || []
        );
      },
      providesTags: () => ["Contacts"],
    }),
    getContact: build.query<
      IContactProfile,
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        url: "api/contacts/get/" + customerId,
        params: {
          orgId: orgId,
        },
      }),
      providesTags: () => ["Contacts"],
    }),
    addContact: build.mutation<unknown, AddIndividualContactRequest>({
      query: (body) => ({
        url: "api/contacts/add-individual",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Contacts"],
    }),
    editContact: build.mutation<unknown, UpdateContactModel>({
      query: (body) => ({
        url: "api/contacts/update-individual",
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        body,
      }),
      onQueryStarted: (_, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(
            billingAPI.util?.invalidateTags(["Invoices", "Payment"]),
          );
        });
      },
      invalidatesTags: ["Contacts"],
    }),
    changeContactPrimaryEmail: build.mutation<
      unknown,
      ChangeContactPrimaryEmailRequest
    >({
      query: ({ contactId, emailId }) => ({
        url: "api/customers/change-primary-email",
        method: "PUT",
        params: {
          contactId,
          emailId,
        },
      }),
      invalidatesTags: ["Contacts"],
    }),
    activateContact: build.mutation<unknown, { contactId: string }>({
      query: (body) => ({
        url: "api/contacts/activate-contact",
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        body: { id: body.contactId },
      }),
      invalidatesTags: ["OrgContacts", "Contacts"],
    }),
    archiveContact: build.mutation<unknown, { contactId: string }>({
      query: (body) => ({
        url: "api/contacts/de-activate-contact",
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        body: { id: body.contactId },
      }),
      invalidatesTags: ["OrgContacts", "Contacts"],
    }),
    deleteContact: build.mutation<unknown, { body: string[]; orgId: string }>({
      query: ({ body, orgId }) => ({
        url: "api/customers/delete",
        method: "DELETE",
        body,
        params: { orgId },
      }),
      invalidatesTags: ["Contacts", "OrgContacts"],
    }),

    //Organizations
    getActiveOrganizations: build.query<IOrganizationContact[], string>({
      query: (orgId = "") => ({
        url: "api/business-contacts/get-all",
        params: {
          orgId: orgId,
        },
      }),
      transformResponse: (res: IOrganizationContact[]) => {
        return (
          res?.filter(
            (item) =>
              item.statusType && item.statusType.statusType === "Active",
          ) || []
        );
      },
      providesTags: () => ["OrgContacts"],
    }),
    searchActiveOrganizations: build.query<
      IOrganizationContact[],
      { orgId: string; searchQuery: string }
    >({
      query: ({ orgId, searchQuery }) => ({
        url: "api/business-contacts/search",
        params: {
          searchQuery: searchQuery,
          orgId: orgId,
        },
      }),
      transformResponse: (res: IOrganizationContact[]) => {
        return (
          res?.filter(
            (item) =>
              item.statusType && item.statusType.statusType === "Active",
          ) || []
        );
      },
      providesTags: () => ["OrgContacts"],
    }),
    getArchivedOrganizations: build.query<IOrganizationContact[], string>({
      query: (orgId = "") => ({
        url: "api/business-contacts/get-all",
        params: {
          orgId: orgId,
        },
      }),
      transformResponse: (res: IOrganizationContact[]) => {
        return (
          res?.filter(
            (item) =>
              item.statusType && item.statusType.statusType === "Unknown",
          ) || []
        );
      },
      providesTags: () => ["OrgContacts"],
    }),
    getAllOrgContacts: build.query<IOrganizationContact[], string>({
      query: (orgId = "") => ({
        url: "api/business-contacts/get-all",
        params: {
          orgId: orgId,
        },
      }),
      providesTags: () => ["OrgContacts"],
    }),
    getOrgContact: build.query<
      IOrganizationContact,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        url: "api/business-contacts/get-by-id/" + id,
        params: {
          orgId: orgId,
        },
      }),
      providesTags: () => ["OrgContacts"],
    }),
    addOrgContact: build.mutation<unknown, AddOrganizationContactRequest>({
      query: (body) => ({
        url: "api/business-contacts/add",
        method: "POST",
        body,
      }),
      invalidatesTags: ["OrgContacts", "Contacts"],
    }),
    editOrgContact: build.mutation<unknown, UpdateOrganizationModel>({
      query: (body) => ({
        url: "api/business-contacts/update-business-contact",
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        body,
      }),
      invalidatesTags: ["OrgContacts", "Contacts"],
    }),
    changeOrganizationPrimaryEmail: build.mutation<
      unknown,
      ChangeOrganizationPrimaryEmailRequest
    >({
      query: ({ businessContactId, emailId }) => ({
        url: "api/business-contacts/change-primary-email",
        method: "PUT",
        params: {
          businessContactId,
          emailId,
        },
      }),
      invalidatesTags: ["OrgContacts", "Contacts"],
    }),
    activateOrganization: build.mutation<unknown, { id: string }>({
      query: (body) => ({
        url: "api/business-contacts/activate-business-contact",
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        body: { id: body.id },
      }),
      invalidatesTags: ["OrgContacts", "Contacts"],
    }),
    archiveOrganization: build.mutation<unknown, { id: string }>({
      query: (body) => ({
        url: "api/business-contacts/deactivate-business-contact",
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        body: { id: body.id },
      }),
      invalidatesTags: ["OrgContacts", "Contacts"],
    }),

    //Imports
    importContacts: build.mutation<
      unknown,
      { body: FormData; locationId: string }
    >({
      query: ({ body, locationId }) => ({
        url: "api/contacts/import-contacts",
        method: "POST",
        params: { locationId },
        body,
      }),
      invalidatesTags: ["Contacts"],
    }),
    importOrganizations: build.mutation<
      unknown,
      { body: FormData; locationId: string }
    >({
      query: ({ body, locationId }) => ({
        url: "api/business-contacts/import-business-contacts",
        method: "POST",
        params: { locationId },
        body,
      }),
      invalidatesTags: ["OrgContacts"],
    }),
    //Common
    mergedContacts: build.mutation<
      unknown,
      { mergeCustomerId: string; mergeToCustomerId: string }
    >({
      query: (params) => ({
        url: "api/customers/merge-customers",
        method: "GET",
        params,
      }),
      onQueryStarted: (_, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(
            billingAPI.util?.invalidateTags(["Invoices", "Payment"]),
          );
          api.dispatch(workSlice.util?.invalidateTags(["Work"]));
          api.dispatch(filesSlice.util?.invalidateTags(["ContactFiles"]));
          api.dispatch(
            linkedProfilesSlice.util?.invalidateTags(["LinkedProfile"]),
          );
          api.dispatch(contactNotesSlice.util?.invalidateTags(["ContactNote"]));
          api.dispatch(
            organizationNotesSlice.util?.invalidateTags(["OrganizationNote"]),
          );
        });
      },
      invalidatesTags: ["Contacts", "OrgContacts", "ContactsWorks"],
    }),
    getContactTypes: build.query<SelectOptionType[], void>({
      query: () => ({
        url: "api/contact-types/all",
        method: "GET",
      }),
      transformResponse: (response: IContactType[]) => {
        const result: SelectOptionType[] = response.map((item) => ({
          value: item?.id,
          label: item?.contactType,
        }));
        return result;
      },
    }),
    getPhoneTypes: build.query<SelectOptionType[], void>({
      query: () => ({
        url: "api/phone-num-types/all",
        method: "GET",
      }),
      transformResponse: (response: IPhoneType[]) => {
        return [...response]
          .sort((left, right) => {
            if (left.phoneNumberType === "Mobile") {
              return -1;
            }

            if (right.phoneNumberType === "Mobile") {
              return 1;
            }

            return 0;
          })
          .map((item) => ({
            value: item.id,
            label: item.phoneNumberType,
          }));
      },
    }),
    getAddIndividualFormData: build.query<AddIndividualFormData, void>({
      query: () => ({
        url: "api/contacts/add-individual",
        method: "GET",
      }),
    }),
    getAddOrganizationFormData: build.query<AddOrganizationFormData, void>({
      query: () => ({
        url: "api/business-contacts/add",
        method: "GET",
      }),
    }),
    getSearchCustomers: build.query<
      CustomerModel[],
      { searchQuery: string; orgId: string; userId: string; isActive?: boolean }
    >({
      query: ({ searchQuery, orgId, userId, isActive }) => ({
        url: "api/customers/search",
        method: "POST",
        body: {
          searchQuery: searchQuery,
          orgId: orgId,
          userId: userId,
          isActive: isActive,
        },
      }),
      providesTags: ["Contacts"],
    }),
    getSearchFilterCustomers: build.query<
      CustomerModel[],
      {
        searchQuery: string;
        orgId: string;
        userId?: string | null;
      }
    >({
      query: ({ searchQuery, orgId, userId }) => ({
        url: "api/customers/search-filter",
        method: "POST",
        body: {
          searchQuery: searchQuery,
          orgId: orgId,
          userId: userId,
          isActive: true,
        },
      }),
      providesTags: ["Contacts"],
    }),
    getOrganizationTypes: build.query<SelectOptionType[], void>({
      query: () => ({
        url: "api/contact-types/organization-types",
        method: "GET",
      }),
      transformResponse: (response: IOrganizationType[]) => {
        const result: SelectOptionType[] = response.map((item) => ({
          value: item?.id,
          label: item?.organizationType,
        }));
        return result;
      },
    }),
    getOrganizationSin: build.query<
      { data: unknown },
      { sin: string; businessNumber: string; orgId: string }
    >({
      query: ({ sin, businessNumber, orgId }) => ({
        url: "api/customers/sin/business-number",
        method: "GET",
        params: {
          sin,
          businessNumber,
          orgId,
        },
      }),
    }),
    getCustomersByEmail: build.query<
      CustomerModel[],
      { email: string; orgId: string }
    >({
      query: ({ email, orgId }) => ({
        url: `api/customers/get-by-email/${email}`,
        method: "GET",
        params: { orgId },
      }),
      providesTags: ["OrgContacts", "Contacts"],
    }),
    // Roadmap 1784: which of the firm's own clients already use these emails
    // (warning only; the old get-by-email ignores the firm).
    checkClientEmails: build.mutation<
      { matches: ClientEmailMatch[] },
      { orgId: string; emails: string[]; exceptCustomerId?: string | null }
    >({
      query: (body) => ({
        url: "api/customers/email-in-use",
        method: "POST",
        body,
      }),
    }),
  }),
});
export const {
  //Contacts
  useGetAllContactsQuery,
  useGetActiveContactsQuery,
  useGetContactQuery,
  useAddContactMutation,
  useEditContactMutation,
  useChangeContactPrimaryEmailMutation,
  useActivateContactMutation,
  useArchiveContactMutation,
  useDeleteContactMutation,

  //Organizations
  useGetAllOrgContactsQuery,
  useSearchActiveOrganizationsQuery,
  useGetActiveOrganizationsQuery,
  useGetOrgContactQuery,
  useLazyGetOrgContactQuery,
  useLazyGetOrganizationSinQuery,
  useAddOrgContactMutation,
  useEditOrgContactMutation,
  useChangeOrganizationPrimaryEmailMutation,
  useActivateOrganizationMutation,
  useArchiveOrganizationMutation,

  //Imports
  useImportContactsMutation,
  useImportOrganizationsMutation,

  //Common
  useMergedContactsMutation,
  useGetContactTypesQuery,
  useGetPhoneTypesQuery,
  useGetAddIndividualFormDataQuery,
  useGetAddOrganizationFormDataQuery,
  useGetOrganizationTypesQuery,

  //Customers (contacts + organizations)
  useGetActiveCustomersQuery,
  useGetArchivedCustomersQuery,
  useActivateDeactivateMutation,
  useGetSearchCustomersQuery,
  useGetSearchFilterCustomersQuery,
  useGetCustomersByEmailQuery,
  useLazyGetCustomersByEmailQuery,
  useCheckClientEmailsMutation,
  useAddPictureMutation,
  useEditPictureMutation,
  useDeletePictureMutation,
} = contactsSlice;
